// Social Activity Feed Item Component
// Displays social interactions and profile updates

import React from "react";
import { motion } from "framer-motion";
import {
  FeedItemContent,
  ContentOverlay,
  UserInfo,
  UserAvatar,
  UserDetails,
  Username,
  Timestamp,
  ContentText,
  ContentTitle,
  ContentDescription,
  ActionButtons,
  ActionButton,
  ActionCount,
} from "../../../styles/SocialFeed/FeedContainer.styles";
import { BaseFeedItemProps, FeedInteraction } from "../../../types/socialFeed";

export const SocialActivityItem: React.FC<BaseFeedItemProps> = ({
  item,
  onInteraction,
  onUserClick,
  onSwipeModeTrigger,
  showSwipeOption = false,
  isFullScreen = false,
  aspectRatio,
}) => {
  const { content, engagement } = item;

  // Get social activity display
  const getSocialActivityDisplay = () => {
    switch (item.type) {
      case "social-activity":
        return {
          icon: "👥",
          title: "Social Activity",
          description: content.description || "Shared a new update",
          color: "#3498db",
        };
      case "profile-update":
        return {
          icon: "📝",
          title: "Profile Update",
          description: content.description || "Updated their profile",
          color: "#9b59b6",
        };
      default:
        return {
          icon: "📱",
          title: "Social Update",
          description: content.description || "New activity",
          color: "#95a5a6",
        };
    }
  };

  const displayData = getSocialActivityDisplay();

  return (
    <FeedItemContent>
      {/* Background with social gradient */}
      <motion.div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(135deg, ${displayData.color}20 0%, ${displayData.color}40 100%)`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {/* Social Activity Icon */}
        <motion.div
          style={{
            fontSize: "3rem",
            marginBottom: "1rem",
          }}
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ 
            duration: 0.6, 
            delay: 0.2,
            type: "spring",
            stiffness: 200 
          }}
        >
          {displayData.icon}
        </motion.div>
      </motion.div>

      {/* Content Overlay */}
      <ContentOverlay>
        <UserInfo>
          <UserAvatar 
            src={item.avatar} 
            alt={item.username}
            onClick={() => onUserClick(item.userId)}
          />
          <UserDetails>
            <Username onClick={() => onUserClick(item.userId)}>
              {item.username}
            </Username>
            <Timestamp>
              {formatTimestamp(item.timestamp)}
            </Timestamp>
          </UserDetails>
        </UserInfo>

        <ContentText>
          <ContentTitle style={{ color: displayData.color }}>
            {displayData.title}
          </ContentTitle>
          <ContentDescription>
            {displayData.description}
          </ContentDescription>
        </ContentText>
      </ContentOverlay>

      {/* Action Buttons */}
      <ActionButtons>
        <ActionButton
          $variant="primary"
          $isActive={engagement.isLiked}
          onClick={() => onInteraction({
            itemId: item.id,
            userId: item.userId,
            type: "like",
            timestamp: new Date(),
          })}
        >
          {engagement.isLiked ? "❤️" : "🤍"}
        </ActionButton>
        <ActionCount>{engagement.likes}</ActionCount>

        <ActionButton
          onClick={() => onInteraction({
            itemId: item.id,
            userId: item.userId,
            type: "comment",
            timestamp: new Date(),
          })}
        >
          💬
        </ActionButton>
        <ActionCount>{engagement.comments}</ActionCount>

        <ActionButton
          onClick={() => onInteraction({
            itemId: item.id,
            userId: item.userId,
            type: "share",
            timestamp: new Date(),
          })}
        >
          📤
        </ActionButton>
        <ActionCount>{engagement.shares}</ActionCount>
      </ActionButtons>
    </FeedItemContent>
  );
};

// Helper function to format timestamp
const formatTimestamp = (timestamp: Date): string => {
  const now = new Date();
  const diff = now.getTime() - new Date(timestamp).getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return "now";
  if (minutes < 60) return `${minutes}m`;
  if (hours < 24) return `${hours}h`;
  if (days < 7) return `${days}d`;
  
  return new Date(timestamp).toLocaleDateString();
};
